// src/hooks/useWebSocket.ts
import { Client, IMessage, StompSubscription } from "@stomp/stompjs";
import { useEffect, useRef } from "react";

export function useWebSocket() {
  const clientRef = useRef<Client | null>(null);
  // Liste des subscriptions demandées (pour réabonnement auto)
  const subscriptionsRef = useRef<
    { destination: string; handler: (m: IMessage) => void }[]
  >([]);
  // Pour éviter plusieurs tentatives de reconnexion simultanées
  const reconnectingRef = useRef(false);
  // Heartbeat interval
  const heartbeatRef = useRef<NodeJS.Timeout | null>(null);

  // Fonction pour activer le client et gérer reconnexion
  const activateClient = () => {
    if (clientRef.current) {
      clientRef.current.deactivate();
    }
    const client = new Client({
      brokerURL: process.env.REACT_APP_WS_URL || "ws://localhost:8080/ws",
      reconnectDelay: 5000,
      beforeConnect: () => {
        const token = localStorage.getItem("jwtToken");
        client.connectHeaders = {
          Authorization: token ? `Bearer ${token}` : "",
        };
      },
      debug: (msg) => console.log("[STOMP]", msg),
      onConnect: () => {
        console.log("STOMP CONNECTED");
        reconnectingRef.current = false;
        // Réabonner à tous les topics demandés
        subscriptionsRef.current.forEach(({ destination, handler }) => {
          client.subscribe(destination, handler);
        });
      },
      onDisconnect: () => {
        console.log("STOMP DISCONNECTED");
        reconnect();
      },
      onStompError: (frame) => {
        console.error("STOMP ERROR", frame);
        reconnect();
      },
    });

    // queue pour les subscribe() appelés avant connexion
    (client as any)._pendingSubscriptions = [];

    client.activate();
    clientRef.current = client;
  };

  // Fonction de reconnexion
  const reconnect = () => {
    if (reconnectingRef.current) return;
    reconnectingRef.current = true;
    setTimeout(() => {
      activateClient();
    }, 2000);
  };

  useEffect(() => {
    activateClient();
    return () => {
      if (clientRef.current) clientRef.current.deactivate();
      if (heartbeatRef.current) clearInterval(heartbeatRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const subscribe = (
    destination: string,
    handler: (m: IMessage) => void
  ): StompSubscription => {
    const client = clientRef.current!;
    // Ajouter à la liste des subscriptions persistées
    if (
      !subscriptionsRef.current.some(
        (s) => s.destination === destination && s.handler === handler
      )
    ) {
      subscriptionsRef.current.push({ destination, handler });
    }
    // si déjà connecté, on s’abonne tout de suite
    if (client.connected) {
      return client.subscribe(destination, handler);
    }
    // sinon, on bufferise mais on stocke aussi la référence pour pouvoir l'annuler
    let subscription: StompSubscription | null = null;
    (client as any)._pendingSubscriptions.push(() => {
      subscription = client.subscribe(destination, handler);
    });
    // on retourne une vraie unsub qui gèrera le cas pending ET le cas connecté
    return {
      id: destination,
      unsubscribe: () => {
        // Retirer de la liste persistée
        subscriptionsRef.current = subscriptionsRef.current.filter(
          (s) => !(s.destination === destination && s.handler === handler)
        );
        if (subscription) {
          subscription.unsubscribe();
        } else {
          (client as any)._pendingSubscriptions = (
            client as any
          )._pendingSubscriptions.filter((fn: any) => fn !== subscription);
        }
      },
    };
  };

  const publish = (destination: string, body: any) => {
    clientRef.current?.publish({
      destination,
      body: JSON.stringify(body),
    });
  };

  return { subscribe, publish };
}
